import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  GraduationCap,
  Layers,
  Presentation,
  Rocket,
  Settings,
  Video,
  X,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { AppButton, Callout, IconButton } from "../ui";

/**
 * WelcomeGuideModal — the first screen a new institute owner sees after login.
 *
 * Six shortcuts into the setup path, not a form: it never collected anything, so
 * paging someone through steps to reach a link they could have clicked was the
 * wrong shape for it.
 *
 * Props:
 *   show       — whether the guide is open
 *   onComplete — called once the guide is dismissed, whichever way
 *
 * ── WHAT WAS ACTUALLY BROKEN ────────────────────────────────────────────────
 * Rewritten 2026-09-08, replacing OnboardingWizard.js. Six defects, of which
 * only the first two were visible in light mode on a tall screen:
 *
 *  1. **The whole card scrolled.** `max-h-[90vh]` and `overflow-y-auto` sat on
 *     the same element, so the title and the only button scrolled away with the
 *     rows. The dialog is a three-part column now — header and footer are
 *     `shrink-0`, and the body is the one thing that scrolls.
 *  2. **Two `max-w` utilities raced.** The card const carried
 *     `tw-max-w-[520px]` and the call site appended `tw-max-w-[580px]`, with
 *     `max-[576px]:tw-max-w-full` written twice on top. Which one won was
 *     decided by emitted source order, not by the className — arbitrary values
 *     have no cascade you can read off the JSX.
 *  3. **A brand FILL was painted with the brand INK.** The Instructors tile was
 *     `tw-bg-primary` + `tw-text-on-status` (white). Dark mode lifts `--primary`
 *     to 63 % L so it reads as ink on a card, which leaves white at 3.56:1 on it
 *     as a fill. Every chip here is `--primary` on a 10 % tint at rest and the
 *     `--primary-solid` / `--primary-solid-fg` PAIR when it fills on hover.
 *  4. **The other five chips were `--chart-*`.** Four of those tokens are
 *     pointers at the status hues, so "Add Students" rendered in `--success` and
 *     "Create a Batch" in `--info` — semantic colour on tiles that mean nothing
 *     of the sort (§4.5). Six shortcuts all do one thing: they act. That is
 *     `--primary`, and the icon carries the identity.
 *  5. **Bootstrap classes inside a Tailwind component** — `text-center py-5`,
 *     `mt-3 text-muted`, `me-2`, `ms-1` — plus react-bootstrap's `Spinner`.
 *     `py-5` is 3rem in Bootstrap and 1.25rem in Tailwind; nothing catches that.
 *  6. **It was not a dialog.** No portal, no `role`, no `aria-modal`, no
 *     Escape, no scroll lock, no backdrop dismiss, and the close control was a
 *     bare icon `<button>` whose only name was a `title`.
 *
 * ⚠️ `onboarding-overlay` is KEPT as a bare marker class with no rule attached.
 * It is not styling — `waitForSettled()` in cypress/e2e/visual.cy.js lists it in
 * its BUSY selector, so dropping the name would blind the screenshot harness to
 * this overlay and let it freeze a baseline mid-onboarding.
 */

const TITLE_ID = "welcome-guide-title";

const QUICK_ACTIONS = [
  {
    icon: BookOpen,
    title: "Create a Course",
    description: "Define what you teach — Web Development, Data Science.",
    path: "/courses",
  },
  {
    icon: Layers,
    title: "Create a Batch",
    description: "Group students into intakes, like a Jan 2026 batch.",
    path: "/batches",
  },
  {
    icon: GraduationCap,
    title: "Add Students",
    description: "Add them one by one, or invite them over email.",
    path: "/students",
  },
  {
    icon: Presentation,
    title: "Add Instructors",
    description: "Invite the people who will teach your courses.",
    path: "/instructors",
  },
  {
    icon: Video,
    title: "Connect Zoom",
    description: "Host live classes without leaving the platform.",
    path: "/settings",
  },
  {
    icon: Settings,
    title: "Institute Settings",
    description: "Branding, fees and the rest of your preferences.",
    path: "/settings",
  },
];

/**
 * One shortcut. A component rather than a class string: the chip's fill, the
 * border and the arrow are three parts of one hover state, and a call site
 * pasting that spec is how six tiles end up looking like five (§11).
 *
 * ⚠️ Every element inside the `<button>` is a `<span>`. A button's content model
 * is PHRASING content, so the `<h6>` and `<p>` this replaces were invalid there —
 * and a heading nested in a control is announced twice by some screen readers,
 * once as a heading and once inside the button's own name. The name here is the
 * title and the description read together, which is what it should be.
 */
const ActionTile = ({ icon: Icon, title, description, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    className="tw-group tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded tw-border tw-border-solid tw-border-line tw-bg-surface-alt tw-p-3.5 tw-text-left tw-transition hover:tw-border-primary tw-focus-ring"
  >
    <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-primary/10 tw-text-primary tw-transition group-hover:tw-bg-primary-solid group-hover:tw-text-primary-solid-fg">
      <Icon size={18} aria-hidden="true" />
    </span>
    <span className="tw-min-w-0">
      <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-text-content">
        {title}
        <ArrowRight
          size={14}
          aria-hidden="true"
          className="tw-shrink-0 tw--translate-x-1 tw-text-primary tw-opacity-0 tw-transition group-hover:tw-translate-x-0 group-hover:tw-opacity-100"
        />
      </span>
      <span className="tw-mt-1 tw-block tw-text-xs tw-leading-relaxed tw-text-content-muted">
        {description}
      </span>
    </span>
  </button>
);

const WelcomeGuideModal = ({ show, onComplete }) => {
  const navigate = useNavigate();
  // Nothing renders while this is true. The check decides whether the guide
  // should exist at all, so a card that may unmount 200ms later is a flash of
  // the wrong screen — which is what the spinner state used to be.
  const [checking, setChecking] = useState(true);

  // ⚠️ `onComplete` is read through a ref, and the effects below depend on
  // `show` alone. Layout builds `handleOnboardingComplete` inline, so its
  // identity changes on every parent render — and the version this replaces
  // held it in the fetch effect's dependencies, which re-ran
  // GET /auth/onboarding-status every time the shell re-rendered underneath an
  // open guide. A dialog should not need its caller to memoise a callback.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  });

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setChecking(true);
    apiClient
      .get("/auth/onboarding-status")
      .then(({ data }) => {
        if (cancelled) return;
        if (data.isComplete || data.institute?.onboardingCompleted) onCompleteRef.current?.();
      })
      .catch(() => {
        // Unreachable status endpoint: show the guide rather than hide it.
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  const open = show && !checking;

  const dismiss = useCallback(async () => {
    try {
      await apiClient.patch("/auth/onboarding-complete");
    } catch {
      // Non-critical — the guide still closes.
    }
    onCompleteRef.current?.();
  }, []);

  // Escape to close, and the page behind must not scroll under the scrim.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") dismiss();
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, dismiss]);

  if (!open) return null;

  const goTo = async (path) => {
    await dismiss();
    navigate(path);
  };

  return createPortal(
    <div
      className="onboarding-overlay tw-fixed tw-inset-0 tw-z-modal tw-flex tw-items-center tw-justify-center tw-overflow-y-auto tw-bg-overlay tw-p-4 tw-backdrop-blur-sm tw-animate-panel-fade"
      onClick={dismiss}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        onClick={(e) => e.stopPropagation()}
        className="tw-flex tw-max-h-[min(92vh,44rem)] tw-w-full tw-max-w-[40rem] tw-flex-col tw-overflow-hidden tw-rounded-lg tw-bg-surface-raised tw-shadow-popover tw-animate-dialog-in motion-reduce:tw-animate-none"
      >
        <div className="tw-flex tw-shrink-0 tw-items-start tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-line-soft tw-px-5 tw-py-4">
          <span className="tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-primary/10 tw-text-primary">
            <Rocket size={20} aria-hidden="true" />
          </span>
          <div className="tw-min-w-0 tw-flex-1">
            <h2
              id={TITLE_ID}
              className="tw-m-0 tw-text-lg tw-font-semibold tw-tracking-tight tw-text-content"
            >
              Welcome to your institute
            </h2>
            <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-leading-relaxed tw-text-content-muted">
              Six things worth setting up. Start anywhere — none of this is a step you have to finish.
            </p>
          </div>
          <IconButton label="Close welcome guide" icon={X} size="sm" onClick={dismiss} />
        </div>

        <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-5 tw-py-4">
          <div className="tw-grid tw-grid-cols-1 tw-gap-2.5 sm:tw-grid-cols-2">
            {QUICK_ACTIONS.map((action) => (
              <ActionTile
                key={action.title}
                icon={action.icon}
                title={action.title}
                description={action.description}
                onSelect={() => goTo(action.path)}
              />
            ))}
          </div>
          <Callout tone="info" className="tw-mt-4">
            All six live in the sidebar too — closing this guide does not put them out of reach.
          </Callout>
        </div>

        <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-end tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-line-soft tw-px-5 tw-py-3.5">
          <AppButton type="button" onClick={dismiss}>
            Got it, let me explore
            <ArrowRight size={15} aria-hidden="true" className="tw-ml-1.5" />
          </AppButton>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WelcomeGuideModal;
